import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Send, 
  Paperclip, 
  FileText, 
  User, 
  Clock, 
  Search,
  ChevronLeft,
  X,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'react-hot-toast';

// Mock data for conversations
const mockConversations = [
  {
    id: '1',
    orderId: 'ORD-123456',
    contractName: 'Freelance Overeenkomst',
    lastMessage: 'Uw contract is klaar. U kunt het nu downloaden.',
    lastMessageDate: '2023-06-20T14:30:00',
    unread: false,
    messages: [
      {
        id: '1-1',
        sender: 'lawyer',
        senderName: 'Mr. Sophie Martin',
        content: 'Hallo, ik ben Mr. Sophie Martin, verantwoordelijk voor het opstellen van uw freelance contract. Aarzel niet om contact met mij op te nemen als u vragen heeft.',
        timestamp: '2023-06-16T10:15:00',
        read: true,
      },
      {
        id: '1-2',
        sender: 'client',
        senderName: 'U',
        content: 'Hallo Mr. Martin, bedankt voor uw bericht. Ik heb een vraag over de intellectuele eigendomsclausule. Behoud ik de rechten op de creaties die ik maak tijdens mijn opdracht?',
        timestamp: '2023-06-16T11:30:00',
        read: true,
      },
      {
        id: '1-3',
        sender: 'lawyer',
        senderName: 'Mr. Sophie Martin',
        content: 'Hallo, in de standaardversie van het contract worden de intellectuele eigendomsrechten overgedragen aan de klant. We kunnen deze clausule echter aanpassen aan uw behoeften. Wilt u bepaalde rechten behouden of de overdracht op een specifieke manier beperken?',
        timestamp: '2023-06-16T14:45:00',
        read: true,
      },
      {
        id: '1-4',
        sender: 'client',
        senderName: 'U',
        content: 'Ik zou graag het recht behouden om de creaties in mijn portfolio en voor professionele presentaties te gebruiken. Is dat mogelijk?',
        timestamp: '2023-06-17T09:20:00',
        read: true,
      },
      {
        id: '1-5',
        sender: 'lawyer',
        senderName: 'Mr. Sophie Martin',
        content: 'Zeker, dat is een redelijke en gebruikelijke vraag. Ik heb een specifieke clausule toegevoegd die het gebruik van de creaties in uw portfolio en voor professionele presentaties toestaat, terwijl de overdracht van commerciële exploitatierechten aan de klant behouden blijft. Het bijgewerkte contract wordt momenteel afgerond.',
        timestamp: '2023-06-17T11:05:00',
        read: true,
      },
      {
        id: '1-6',
        sender: 'lawyer',
        senderName: 'Mr. Sophie Martin',
        content: 'Uw contract is klaar. U kunt het nu downloaden.',
        timestamp: '2023-06-20T14:30:00',
        read: true,
        attachment: {
          name: 'Freelance_Overeenkomst_Definitief.pdf',
          size: '1.2 MB',
        },
      },
    ],
  },
  {
    id: '2',
    orderId: 'ORD-234567',
    contractName: 'Geheimhoudingsovereenkomst (NDA)',
    lastMessage: 'Ik heb wat aanvullende informatie nodig over de aard van de vertrouwelijke informatie.',
    lastMessageDate: '2023-06-24T16:45:00',
    unread: true,
    messages: [
      {
        id: '2-1',
        sender: 'lawyer',
        senderName: 'Mr. Thomas Dubois',
        content: 'Hallo, ik ben Mr. Thomas Dubois, verantwoordelijk voor het opstellen van uw geheimhoudingsovereenkomst. Ik heb uw antwoorden op de vragenlijst bekeken en heb wat aanvullende informatie nodig.',
        timestamp: '2023-06-23T15:30:00',
        read: true,
      },
      {
        id: '2-2',
        sender: 'lawyer',
        senderName: 'Mr. Thomas Dubois',
        content: 'Ik heb wat aanvullende informatie nodig over de aard van de vertrouwelijke informatie. Kunt u meer details geven over de soorten technische gegevens die gedeeld zullen worden?',
        timestamp: '2023-06-24T16:45:00',
        read: false,
      },
    ],
  },
];

const ClientMessages: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialOrderId = queryParams.get('order');
  
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobileListVisible, setIsMobileListVisible] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Fetch conversations
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchConversations = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setConversations(mockConversations);
        
        // If order ID is provided in URL, select that conversation
        if (initialOrderId) {
          const conversation = mockConversations.find(c => c.orderId === initialOrderId);
          if (conversation) {
            setSelectedConversation(conversation);
            setIsMobileListVisible(false);
          }
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversations();
  }, [initialOrderId]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation]);
  
  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && selectedConversation.unread) {
      // In a real app, this would be an API call to mark messages as read
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, unread: false, messages: conv.messages.map((msg: any) => ({ ...msg, read: true })) } 
            : conv
        )
      );
      
      setSelectedConversation(prev => 
        prev ? { ...prev, unread: false, messages: prev.messages.map((msg: any) => ({ ...msg, read: true })) } : null
      );
    }
  }, [selectedConversation]);
  
  const handleSelectConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    setIsMobileListVisible(false);
  };
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || !selectedConversation) return;
    
    const newMessage = {
      id: `${selectedConversation.id}-${selectedConversation.messages.length + 1}`,
      sender: 'client',
      senderName: 'U',
      content: message,
      timestamp: new Date().toISOString(),
      read: true,
    };
    
    // Update conversations state
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.id === selectedConversation.id 
          ? { 
              ...conv, 
              messages: [...conv.messages, newMessage],
              lastMessage: message,
              lastMessageDate: new Date().toISOString(),
            } 
          : conv
      )
    );
    
    // Update selected conversation
    setSelectedConversation(prev => 
      prev ? { 
        ...prev, 
        messages: [...prev.messages, newMessage],
        lastMessage: message,
        lastMessageDate: new Date().toISOString(),
      } : null
    );
    
    // Clear input
    setMessage('');
    
    // In a real app, this would be an API call to send the message
    toast.success('Bericht verzonden');
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleBackToList = () => {
    setIsMobileListVisible(true);
  };
  
  // Filter conversations based on search term
  const filteredConversations = conversations.filter(conv => 
    conv.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.contractName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Berichten</h1>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex h-[600px]">
          {/* Conversation List - Hidden on mobile when a conversation is selected */}
          <div className={`w-full md:w-1/3 border-r border-gray-200 ${isMobileListVisible ? 'block' : 'hidden md:block'}`}>
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Zoeken..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            
            <div className="overflow-y-auto h-[calc(600px-73px)]">
              {filteredConversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Geen gesprekken gevonden
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {filteredConversations.map((conversation) => (
                    <li 
                      key={conversation.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''}`}
                      onClick={() => handleSelectConversation(conversation)}
                    >
                      <div className="p-4 relative">
                        <div className="flex justify-between">
                          <h3 className="text-sm font-medium text-gray-900">{conversation.orderId}</h3>
                          <span className="text-xs text-gray-500">
                            {format(new Date(conversation.lastMessageDate), 'dd/MM/yyyy')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{conversation.contractName}</p>
                        <p className="text-sm text-gray-500 mt-1 truncate">{conversation.lastMessage}</p>
                        
                        {conversation.unread && (
                          <span className="absolute top-4 right-4 h-3 w-3 bg-blue-600 rounded-full"></span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          
          {/* Conversation Detail */}
          <div className={`w-full md:w-2/3 flex flex-col ${!isMobileListVisible ? 'block' : 'hidden md:flex'}`}>
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-gray-200 flex items-center">
                  <button
                    onClick={handleBackToList}
                    className="md:hidden text-gray-500 hover:text-gray-700 mr-2"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{selectedConversation.orderId}</h3>
                    <p className="text-sm text-gray-600">{selectedConversation.contractName}</p>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                  <div className="space-y-4">
                    {selectedConversation.messages.map((msg: any) => (
                      <div 
                        key={msg.id} 
                        className={`flex ${msg.sender === 'client' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-xs md:max-w-md rounded-lg p-4 ${
                            msg.sender === 'client' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white text-gray-800 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-sm">{msg.senderName}</span>
                            <span className="text-xs opacity-75 ml-2">
                              {format(new Date(msg.timestamp), 'HH:mm')}
                            </span>
                          </div>
                          
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {msg.attachment && (
                            <div className={`mt-2 p-2 rounded-md flex items-center ${
                              msg.sender === 'client' ? 'bg-blue-700' : 'bg-gray-100'
                            }`}>
                              <FileText className={`h-5 w-5 ${
                                msg.sender === 'client' ? 'text-blue-200' : 'text-blue-600'
                              } mr-2`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${
                                  msg.sender === 'client' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {msg.attachment.name}
                                </p>
                                <p className={`text-xs ${
                                  msg.sender === 'client' ? 'text-blue-200' : 'text-gray-500'
                                }`}>
                                  {msg.attachment.size}
                                </p>
                              </div>
                              <button className={`${
                                msg.sender === 'client' ? 'text-white hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                              }`}>
                                <Download className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                
                <div className="p-4 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSendMessage} className="flex items-center">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 mr-2"
                      title="Bestand bijvoegen"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    
                    <input
                      type="text"
                      placeholder="Schrijf uw bericht..."
                      className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    
                    <button
                      type="submit"
                      disabled={!message.trim()}
                      className="ml-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md p-2 disabled:opacity-50"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-50">
                <MessageSquare className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Geen gesprek geselecteerd</h3>
                <p className="text-gray-600 text-center max-w-md mb-6">
                  Selecteer een gesprek in de lijst of start een nieuw gesprek vanuit uw bestelling.
                </p>
                <Link
                  to="/orders"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300"
                >
                  Bekijk mijn bestellingen
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientMessages;